import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { CSSProperties, HTMLAttributes, useState } from "react"
import { getRotatedIconsHardCodedValues, zIndex } from "@nova/constants"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { formatIconUrl } from "./icons/module"

type InspectorPanelKeyValueDropdownProps = {
    id: string
    isOpen: boolean
    setOpenDropdownId: (id: string | undefined) => void

    keyTitle: string
    dropdownTitle?: string

    // If icon is from the component-icons package.
    Icon?: Icons.IconComponent
    iconClasses?: string
    iconStyle?: CSSProperties
    /** Hex code including # */
    iconAccentColor?: string

    childrenAreFullWidth?: boolean
    align?: "left" | "right" | "center"

    // If icon is from the /public folder.
    iconUrl?: string

    /**
     * Set this to true if this component is being used
     * in an accordion. It will adjust the styles
     * accordingly.
     */
    isInSubSection?: boolean
    includeSpacer?: boolean
    disabled?: boolean
} & HTMLAttributes<HTMLDivElement>

export const InspectorPanelKeyValueDropdown = (props: InspectorPanelKeyValueDropdownProps) => {
    const {
        dropdownTitle,
        Icon,
        iconUrl,
        isInSubSection,
        includeSpacer,
        disabled,
        keyTitle,
        className,
    } = props
    if (!dropdownTitle && !Icon && !iconUrl)
        throw Error("Must provide dropdownTitle or Icon or iconUrl.")

    return (
        <div className={classNames("flex-none h-8 relative", className)}>
            {/* Key & Dropdown */}
            <div className={classNames("flex items-center w-full", { "gap-3": !isInSubSection })}>
                {/* Field Key */}
                <h5
                    className={classNames("w-24 select-none flex-none", {
                        "text-gray-0": !disabled && !isInSubSection,
                        "text-gray-100": !disabled && isInSubSection,
                        "text-gray-500": disabled,
                    })}
                >
                    {keyTitle}
                </h5>

                {/* Spacer */}
                {(isInSubSection || includeSpacer) && <div className="flex-grow" />}

                <DropdownButton {...props} />
            </div>
        </div>
    )
}

const DropdownButton = ({
    id,
    isOpen,
    disabled,
    Icon,
    setOpenDropdownId,
    iconStyle,
    iconAccentColor,
    iconClasses,
    iconUrl,
    dropdownTitle,
    align = "center",
    ...rest
}: InspectorPanelKeyValueDropdownProps) => {
    const [isHovering, setIsHovering] = useState(false)

    return (
        <div className="relative">
            <div
                className={classNames("flex h-8 gap-1 items-center rounded-md border", {
                    "border-gray-500": (isHovering || isOpen) && !disabled,
                    "border-transparent": (!isHovering && !isOpen) || disabled,
                    "bg-gray-500": isOpen,
                    "px-1": !!Icon,
                    "pl-2 pr-1": !Icon,
                    "cursor-pointer": !disabled,
                    "cursor-not-allowed": disabled,
                })}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onClick={!disabled ? () => setOpenDropdownId(isOpen ? undefined : id) : undefined}
            >
                {/* Dropdown Icon */}
                {Icon ? (
                    <Icons.SmallIcon
                        Icon={Icon}
                        className={classNames("text-gray-0", iconClasses)}
                        style={{ ...iconStyle }}
                        accentColor={iconAccentColor}
                    />
                ) : iconUrl ? (
                    <svg
                        data-src={`/${formatIconUrl(iconUrl)}`}
                        width={20}
                        height={20}
                        className={classNames("text-gray-0", iconClasses)}
                        style={{
                            ...iconStyle,
                            transform: `rotate(${getRotatedIconsHardCodedValues(iconUrl)}deg)`,
                        }}
                    />
                ) : null}

                {/* Dropdown Title */}
                <small
                    className="text-gray-0 select-none"
                    style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}
                >
                    {dropdownTitle}
                </small>

                {/* Dropdown Arrow */}
                <Icons.ExtraSmallIcon
                    Icon={Icons.ArrowDropdown}
                    className={classNames({
                        "text-gray-300": !isHovering && !isOpen && !disabled,
                        "text-gray-0": (isHovering || isOpen) && !disabled,
                        "text-gray-500": disabled,
                    })}
                />
            </div>
            {/* Dropdown Component */}
            {isOpen && (
                <ClickAwayListener onClickAway={() => setOpenDropdownId(undefined)}>
                    <div
                        className={classNames("absolute w-fit mt-1", {
                            "left-0": align === "left",
                            "right-0": align === "right",
                            "left-1/2 -translate-x-1/2": align === "center",
                        })}
                        style={{ zIndex: zIndex.InspectorPanel + 50 }}
                    >
                        {rest.children}
                    </div>
                </ClickAwayListener>
            )}
        </div>
    )
}
